/**
 * Milestones view (Doc 17 §10): milestones, at-risk indicators, and "What slips if this slips?".
 * Plan-cycle scoped — milestones use EmbeddedListTable; work-unit impact uses a
 * natural-height list (virtualized fixed rows cannot host expandable content).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { EntityListShell } from '../../components/collection/EntityListShell';
import { EmbeddedListTable } from '../../components/collection/EmbeddedListTable';
import type { ListTableColumnDef } from '../../components/collection/ListTable';
import { getSupabaseClient } from '../../lib/supabaseClient';
import {
  getCriticalPathWorkUnitIds,
  getAtRiskWorkUnitIds,
  getDownstreamWorkUnitIds,
} from '../../lib/planningQueries';
import type { Milestone, WorkUnit } from '../../types/planning';
import {
  PlanningCycleScope,
  type PlanningCycleContext,
} from '../../components/planning/PlanningCycleScope';
import { AddMilestoneControl } from '../../components/planning/AddMilestoneControl';

function WorkUnitImpactList({
  workUnits,
  criticalPathIds,
  atRiskIds,
  expandedSlips,
  downstreamByWuId,
  loadingSlips,
  wuById,
  onToggleSlips,
  emptyTitle,
  emptyDescription,
}: {
  workUnits: WorkUnit[];
  criticalPathIds: Set<string>;
  atRiskIds: Set<string>;
  expandedSlips: Set<string>;
  downstreamByWuId: Record<string, string[]>;
  loadingSlips: Set<string>;
  wuById: Record<string, WorkUnit>;
  onToggleSlips: (id: string) => void;
  emptyTitle: string;
  emptyDescription: string;
}) {
  if (workUnits.length === 0) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-6 text-center">
        <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">{emptyTitle}</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <ul className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] overflow-hidden">
      {workUnits.map((wu) => {
        const isAtRisk = atRiskIds.has(wu.id);
        const isCriticalPath = criticalPathIds.has(wu.id);
        const expanded = expandedSlips.has(wu.id);
        const slipsList = downstreamByWuId[wu.id];
        const loadingSlip = loadingSlips.has(wu.id);

        return (
          <li key={wu.id} className="px-3 py-3">
            <div className="flex items-start gap-2 min-w-0">
              <button
                type="button"
                onClick={() => void onToggleSlips(wu.id)}
                className="mt-0.5 p-0.5 rounded text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] flex-shrink-0"
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide downstream impact' : 'Show what slips if this slips'}
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>

              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className="font-medium text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate min-w-0">
                    {wu.title}
                  </span>
                  {isCriticalPath && (
                    <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                      Critical path
                    </span>
                  )}
                  {isAtRisk && (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)]">
                      <AlertTriangle className="h-3.5 w-3.5" /> At risk
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => void onToggleSlips(wu.id)}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {loadingSlip ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Fetching downstream…
                    </span>
                  ) : expanded ? (
                    'Hide downstream'
                  ) : (
                    'What slips if this slips?'
                  )}
                </button>

                {expanded && slipsList !== undefined && (
                  <div className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] pl-0.5">
                    {slipsList.length === 0 ? (
                      <p className="italic text-[var(--text-ter)] dark:text-[var(--text-dim)]">Nothing downstream.</p>
                    ) : (
                      <ul className="list-disc list-inside space-y-1">
                        {slipsList.map((id) => (
                          <li key={id}>{wuById[id]?.title ?? `${id.slice(0, 8)}…`}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function MilestonesContent({ ctx }: { ctx: PlanningCycleContext }) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [criticalPathIds, setCriticalPathIds] = useState<Set<string>>(new Set());
  const [atRiskIds, setAtRiskIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSlips, setExpandedSlips] = useState<Set<string>>(new Set());
  const [downstreamByWuId, setDownstreamByWuId] = useState<Record<string, string[]>>({});
  const [loadingSlips, setLoadingSlips] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    if (!ctx.planVersionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const supabase = getSupabaseClient();
    const [milRes, initRes] = await Promise.all([
      supabase.from('milestones').select('*').eq('plan_version_id', ctx.planVersionId),
      supabase.from('initiatives').select('id').eq('plan_version_id', ctx.planVersionId),
    ]);
    const inits = (initRes.data ?? []) as { id: string }[];
    setMilestones((milRes.data ?? []) as Milestone[]);

    if (inits.length === 0) {
      setWorkUnits([]);
      setCriticalPathIds(new Set());
      setAtRiskIds(new Set());
      setLoading(false);
      return;
    }

    const wuRes = await supabase
      .from('work_units')
      .select('*')
      .in('initiative_id', inits.map((i) => i.id));

    const wuList = (wuRes.data ?? []) as WorkUnit[];
    setWorkUnits(wuList);

    const [cp, atRisk] = await Promise.all([
      getCriticalPathWorkUnitIds(ctx.planVersionId),
      getAtRiskWorkUnitIds(ctx.planVersionId),
    ]);
    setCriticalPathIds(cp.data ?? new Set());
    setAtRiskIds(atRisk.data ?? new Set());
    setLoading(false);
  }, [ctx.planVersionId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const toggleSlips = useCallback(
    async (workUnitId: string) => {
      if (downstreamByWuId[workUnitId] !== undefined) {
        setExpandedSlips((prev) => {
          const next = new Set(prev);
          if (next.has(workUnitId)) next.delete(workUnitId);
          else next.add(workUnitId);
          return next;
        });
        return;
      }
      setLoadingSlips((prev) => new Set(prev).add(workUnitId));
      const { data } = await getDownstreamWorkUnitIds(ctx.planVersionId, workUnitId);
      setDownstreamByWuId((prev) => ({ ...prev, [workUnitId]: data ?? [] }));
      setLoadingSlips((prev) => {
        const next = new Set(prev);
        next.delete(workUnitId);
        return next;
      });
      setExpandedSlips((prev) => new Set(prev).add(workUnitId));
    },
    [ctx.planVersionId, downstreamByWuId],
  );

  const q = searchQuery.trim().toLowerCase();
  const filteredMilestones = useMemo(
    () =>
      q
        ? milestones.filter(
            (m) =>
              m.name.toLowerCase().includes(q) ||
              (m.success_criteria?.toLowerCase().includes(q) ?? false),
          )
        : milestones,
    [milestones, q],
  );
  const filteredWorkUnits = useMemo(
    () => (q ? workUnits.filter((w) => w.title.toLowerCase().includes(q)) : workUnits),
    [workUnits, q],
  );

  const wuById = useMemo(
    () => Object.fromEntries(workUnits.map((w) => [w.id, w])),
    [workUnits],
  );

  const atRiskCount = workUnits.filter((w) => atRiskIds.has(w.id)).length;

  const milestoneColumns = useMemo((): ListTableColumnDef<Milestone>[] => [
    {
      key: 'name',
      label: 'Name',
      defaultWidth: 200,
      render: (m) => (
        <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] block truncate min-w-0 w-full">
          {m.name}
        </span>
      ),
    },
    {
      key: 'target_date',
      label: 'Target',
      defaultWidth: 120,
      render: (m) =>
        m.target_date ? (
          <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {new Date(m.target_date).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-[var(--text-ter)] dark:text-[var(--text-dim)]">—</span>
        ),
    },
    {
      key: 'success_criteria',
      label: 'Success criteria',
      defaultWidth: 280,
      render: (m) => (
        <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] block truncate min-w-0 w-full">
          {m.success_criteria ?? '—'}
        </span>
      ),
    },
  ], []);

  return (
    <EntityListShell
      className="h-full"
      hideToolbar
      icon={<Calendar className="h-4 w-4 text-primary-600" />}
      iconBgClassName="bg-primary-50 dark:bg-primary-900/20"
      title="Milestones"
      subtitle={`${milestones.length} milestone${milestones.length !== 1 ? 's' : ''} · ${workUnits.length} work unit${workUnits.length !== 1 ? 's' : ''}${atRiskCount > 0 ? ` · ${atRiskCount} at risk` : ''}`}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search milestones and work units…"
      onRefresh={() => void loadData()}
      refreshing={loading}
      contextBar={{
        label: `Plan version ${ctx.planVersionId.slice(0, 8)}…`,
      }}
    >
      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-8">
        {loading && milestones.length === 0 && workUnits.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <AddMilestoneControl
                planVersionId={ctx.planVersionId}
                onAdded={(m) => setMilestones((prev) => [...prev, m])}
              />
            </div>
            <EmbeddedListTable
              title="Milestones"
              items={filteredMilestones}
              getId={(m) => m.id}
              columns={milestoneColumns}
              loading={loading}
              emptyTitle="No milestones"
              emptyDescription="Add a schedule anchor with a target date and success criteria."
            />

            <section>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
                Work units & schedule impact
              </h2>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-3">
                At-risk indicators from critical path and buffer; expand a row to see what slips downstream.
              </p>
              {loading && filteredWorkUnits.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)]" />
                </div>
              ) : (
                <WorkUnitImpactList
                  workUnits={filteredWorkUnits}
                  criticalPathIds={criticalPathIds}
                  atRiskIds={atRiskIds}
                  expandedSlips={expandedSlips}
                  downstreamByWuId={downstreamByWuId}
                  loadingSlips={loadingSlips}
                  wuById={wuById}
                  onToggleSlips={toggleSlips}
                  emptyTitle="No work units"
                  emptyDescription="Work units for this plan version will appear here once initiatives are decomposed."
                />
              )}
            </section>

            <div className="flex flex-wrap gap-4 text-sm">
              <Link to={`/planning/plans/${ctx.cycleId}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                View plan →
              </Link>
              <Link
                to={`/planning/work-graph?cycleId=${ctx.cycleId}`}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Work graph →
              </Link>
            </div>
          </>
        )}
      </div>
    </EntityListShell>
  );
}

export default function PlanningMilestones() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams] = useSearchParams();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const resolvedCycleId = cycleIdFromParams ?? searchParams.get('cycleId');

  return (
    <div className="h-full min-h-[520px]">
      <PlanningCycleScope
        cycleIdParam={resolvedCycleId}
        selectedCycleId={selectedCycleId}
        onSelectCycle={(id) => setSelectedCycleId(id)}
      >
        {(ctx) => <MilestonesContent ctx={ctx} />}
      </PlanningCycleScope>
    </div>
  );
}
